import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_GROUPS, PRIMARY_LINKS, SECONDARY_LINKS } from "@/site";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null); // desktop dropdown key
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  // Close everything on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
    setOpenMobileGroup(null);
  }, [pathname]);

  const handleEnter = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenGroup(key);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenGroup(null), 140);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-[36px] inset-x-0 z-50 bg-[#09090b]/95 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-[76px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group shrink-0"
          onClick={() => setMobileOpen(false)}
        >
          <img
            src="/images/clb-logo.jpeg"
            alt="CLB Solution LLC — Coach · Learn · Build"
            className="h-14 md:h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          <TopLink to="/" label="Home" exact testid="nav-link-home" />

          {NAV_GROUPS.map((g) => (
            <div
              key={g.key}
              className="relative"
              onMouseEnter={() => handleEnter(g.key)}
              onMouseLeave={handleLeave}
            >
              <button
                type="button"
                data-testid={`nav-group-${g.key}`}
                aria-expanded={openGroup === g.key}
                onClick={() => setOpenGroup((k) => (k === g.key ? null : g.key))}
                className={[
                  "flex items-center gap-1 px-3 py-2 text-[12px] uppercase tracking-[0.16em] font-semibold transition-colors",
                  openGroup === g.key ? "text-[#FFCC00]" : "text-white/75 hover:text-white",
                ].join(" ")}
              >
                {g.label}
                <ChevronDown
                  size={13}
                  className={`transition-transform duration-200 ${openGroup === g.key ? "rotate-180" : ""}`}
                />
              </button>

              {openGroup === g.key && (
                <div
                  data-testid={`nav-dropdown-${g.key}`}
                  onMouseEnter={() => handleEnter(g.key)}
                  onMouseLeave={handleLeave}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[380px] z-50"
                >
                  <div className="bg-[#0c0c0e] border border-white/10 shadow-2xl">
                    <div className="p-5 border-b border-white/10">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] font-semibold">
                        {g.label}
                      </div>
                      <div className="mt-1 text-sm text-white/60">{g.description}</div>
                    </div>
                    <ul className="p-2">
                      {g.items.map((it) => (
                        <li key={`${g.key}-${it.to}-${it.label}`}>
                          <Link
                            to={it.to}
                            data-testid={`nav-item-${g.key}-${it.to.replace(/\//g, "")}`}
                            onClick={() => setOpenGroup(null)}
                            className="group block px-3 py-3 hover:bg-white/[0.04] transition-colors"
                          >
                            <div className="font-display text-sm font-bold uppercase tracking-tight text-white group-hover:text-[#FFCC00] transition-colors">
                              {it.label}
                            </div>
                            {it.desc && (
                              <div className="text-xs text-white/50 mt-0.5 leading-snug">{it.desc}</div>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}

          <TopLink to="/pretrip-guide" label="Pretrip App" testid="nav-link-pretrip-app" highlight />
          <TopLink to="/contact" label="Contact" testid="nav-link-contact" />
        </nav>

        <div className="hidden lg:block ml-3">
          <Link
            to="/contact"
            data-testid="header-cta-contact"
            className="bg-[#FFCC00] text-black font-bold uppercase tracking-[0.16em] text-xs px-5 py-3 hover:bg-[#E6B800] transition-colors"
          >
            Request Info
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-white"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-white/10 bg-[#09090b]" data-testid="mobile-menu">
          <div className="max-h-[75vh] overflow-y-auto px-4 py-4 space-y-1">
            {PRIMARY_LINKS.map((l) => (
              <MobileLink key={l.to + l.label} to={l.to} label={l.label} onClick={() => setMobileOpen(false)} />
            ))}

            {NAV_GROUPS.map((g) => {
              const isOpen = openMobileGroup === g.key;
              return (
                <div key={g.key} className="border-l-2 border-transparent">
                  <button
                    type="button"
                    data-testid={`mobile-group-${g.key}`}
                    onClick={() => setOpenMobileGroup((k) => (k === g.key ? null : g.key))}
                    className={[
                      "w-full flex items-center justify-between py-3 px-3 text-sm uppercase tracking-[0.16em] font-semibold border-l-2",
                      isOpen ? "border-[#FFCC00] text-[#FFCC00] bg-white/[0.03]" : "border-transparent text-white/85",
                    ].join(" ")}
                  >
                    {g.label}
                    <ChevronDown size={14} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <ul className="pl-6 pr-2 py-1 space-y-0.5 border-l-2 border-[#FFCC00]/40 ml-3">
                      {g.items.map((it) => (
                        <li key={`m-${g.key}-${it.to}-${it.label}`}>
                          <Link
                            to={it.to}
                            data-testid={`mobile-item-${g.key}-${it.to.replace(/\//g, "")}`}
                            onClick={() => setMobileOpen(false)}
                            className="block py-2.5 px-3 text-[13px] text-white/75 hover:text-[#FFCC00] transition-colors"
                          >
                            {it.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}

            <div className="pt-3 mt-2 border-t border-white/10">
              {SECONDARY_LINKS.map((l) => (
                <MobileLink key={l.to} to={l.to} label={l.label} onClick={() => setMobileOpen(false)} secondary />
              ))}
            </div>

            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-cta-contact"
              className="mt-4 block bg-[#FFCC00] text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-4 text-center"
            >
              Request Info
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function TopLink({ to, label, testid, exact = false, highlight = false }) {
  return (
    <NavLink
      to={to}
      end={exact}
      data-testid={testid}
      className={({ isActive }) =>
        [
          "px-3 py-2 text-[12px] uppercase tracking-[0.16em] font-semibold transition-colors",
          highlight
            ? isActive
              ? "text-[#FFCC00]"
              : "text-[#FFCC00]/85 hover:text-[#FFCC00]"
            : isActive
              ? "text-[#FFCC00]"
              : "text-white/75 hover:text-white",
        ].join(" ")
      }
    >
      {label}
    </NavLink>
  );
}

function MobileLink({ to, label, onClick, secondary = false }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onClick}
      data-testid={`mobile-link-${to === "/" ? "home" : to.replace(/\//g, "")}`}
      className={({ isActive }) =>
        [
          "block py-3 px-3 border-l-2 uppercase tracking-[0.16em] font-semibold",
          secondary ? "text-[12px]" : "text-sm",
          isActive
            ? "border-[#FFCC00] text-[#FFCC00] bg-white/[0.03]"
            : "border-transparent text-white/80 hover:text-white hover:bg-white/[0.03]",
        ].join(" ")
      }
    >
      {label}
    </NavLink>
  );
}
